import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { FormikHelpers } from "formik";
import { ReactElement, useCallback, useState } from "react";
import * as Yup from "yup";

import { NonCampaignInboxType } from "~frontend/__generated__/graphql";
import { InternalLink } from "~frontend/components/Link";
import {
  FormikDialog,
  FormikErrorBanner,
  FormikTextField,
  submitErrorField,
} from "~frontend/components/forms";
import {
  useSubaccount,
  useSubaccountAdminConfig,
} from "~frontend/providers/SubaccountProvider";
import { useErrorMessageFormatter } from "~frontend/util/errors";

import { FormikInboxMembersField } from "./FormikInboxMembersField";
import { InboxTexterSettings } from "./InboxTexterSettings";
import {
  AddOrEditInboxFormValues,
  EscalationInbox,
  GeneralInbox,
} from "./types";

export function EditInboxDialog({
  open,
  onClose,
  onSubmit,
  inbox,
  inboxType,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: AddOrEditInboxFormValues) => Promise<void>;
  inbox: EscalationInbox | GeneralInbox;
  inboxType: NonCampaignInboxType;
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();
  const { subaccountLink } = useSubaccount()!;
  const subaccount = useSubaccountAdminConfig()!;

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (
      values: AddOrEditInboxFormValues,
      formik: FormikHelpers<AddOrEditInboxFormValues>
    ) => {
      if (submitting) {
        return;
      }

      setSubmitting(true);
      try {
        await onSubmit(values);
        formik.resetForm();
      } catch (e) {
        formik.setErrors({ [submitErrorField]: getErrorMessage(e) });
      } finally {
        setSubmitting(false);
      }
    },
    [submitting, setSubmitting, onSubmit, getErrorMessage]
  );

  let dialogTitle = "";
  let nameFieldLabel = "";
  switch (inboxType) {
    case NonCampaignInboxType.Escalation:
      dialogTitle = "Edit Escalation";
      nameFieldLabel = "Escalation Name";
      break;
    case NonCampaignInboxType.General:
      dialogTitle = "Edit Inbox";
      nameFieldLabel = "Inbox Name";
      break;
    default:
      inboxType satisfies never;
  }

  return (
    <FormikDialog<AddOrEditInboxFormValues>
      open={open}
      initialValues={{
        [submitErrorField]: null,
        name: inbox.name!,
        members: inbox.userInboxes.map(ui => ui.user),
        texterAssignmentBatchSizeOverrideSubaccount:
          !!inbox.texterAssignmentBatchSize,
        texterAssignmentBatchSize:
          inbox.texterAssignmentBatchSize ??
          subaccount.texterAssignmentBatchSize,
        texterAssignmentExpirationMinutesOverrideSubaccount:
          !!inbox.texterAssignmentExpirationMinutes,
        texterAssignmentExpirationMinutes:
          inbox.texterAssignmentExpirationMinutes ??
          subaccount.texterAssignmentExpirationMinutes,
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().required("Name is required"),
        members: Yup.array()
          .of(
            Yup.object().shape({
              id: Yup.string().required(),
              fullName: Yup.string().required(),
            })
          )
          .min(1, "Inbox must have at least one member"),
        texterAssignmentBatchSizeOverrideSubaccount: Yup.bool().required(),
        texterAssignmentBatchSize: Yup.number().when(
          "texterAssignmentBatchSizeOverrideSubaccount",
          {
            is: true,
            then: Yup.number().required("Required"),
          }
        ),
        texterAssignmentExpirationMinutesOverrideSubaccount:
          Yup.bool().required(),
        texterAssignmentExpirationMinutes: Yup.number().when(
          "texterAssignmentExpirationMinutesOverrideSubaccount",
          {
            is: true,
            then: Yup.number().required("Required"),
          }
        ),
      })}
      onSubmit={handleSubmit}
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <FormikErrorBanner />
        <FormikTextField name="name" label={nameFieldLabel} fullWidth />
        <FormikInboxMembersField name="members" />
        <Typography variant="body1" sx={{ marginBottom: "16px" }}>
          Select people who should have access to this inbox. An inbox member
          must already have an account (viewable on the{" "}
          <InternalLink to={subaccountLink("/users")}>People page</InternalLink>
          ) to be added to an inbox.
        </Typography>
        <InboxTexterSettings />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="tertiary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary">
          Save Changes
        </Button>
      </DialogActions>
    </FormikDialog>
  );
}
